import React from 'react';
import { Box } from '@chakra-ui/react';

import { getFormatDateFromObject, getDateFromString } from 'shared-components';
import { IRisk } from 'types';

import { SummaryRow } from '../../../Summary/SummaryRow';
import { isFundingRisk } from '../../../../utilities/risks';
import { useTranslation } from "react-i18next";

interface Props {
  id: string;
  showRisks: boolean;
  risk: IRisk['financeDocsRisk']['educationSponsorship'];
  hasOfficialLetterhead: boolean;
  nameAndLogoVisible: boolean;
  officiallySigned: boolean;
  hasDate: boolean;
  dateOnLetter: string;
  selectedTextBodyReq: string[];
  hasContactDetails: boolean;
  fundConditions: boolean;
  isMoreThan6MonthsOld: boolean;
  uploadedBy?: string;
}

export default function LoanLetter({
  id,
  showRisks,
  risk,
  hasOfficialLetterhead,
  nameAndLogoVisible,
  officiallySigned,
  hasDate,
  dateOnLetter,
  selectedTextBodyReq,
  hasContactDetails,
  fundConditions,
  isMoreThan6MonthsOld,
  uploadedBy,
}: Props) {
    const { t } = useTranslation('forms.finance');
  const getBodyTextAnswers = () => {
    const questions = [
      {
        key: 'fullName',
        value: 'Full name matches passport',
      },
      {
        key: 'orgName',
        value: 'Loan provider',
      },
      {
        key: 'courseName',
        value: 'Course name',
      },
      {
        key: 'contributionAmount',
        value: 'Contribution amount',
      },
      {
        key: 'periodCovered',
        value: 'Period covered',
      },
      {
        key: 'loanName',
        value: 'Loan type and regulatory body',
      },
      {
        key: 'fundsReleased',
        value: 'When funds will be released',
      },
    ];

    const answers: React.ReactNode[] = [];
    questions.forEach((question, index) => {
      if (selectedTextBodyReq.includes(question.key)) {
        answers.push(<Box key={`body-text-answer-${index}`}>{question.value}</Box>);
      } else {
        answers.push(
          <Box color={'warning'} key={`body-text-answer-not-selected-${index}`}>
            {question.value}
          </Box>,
        );
      }
    });

    return answers;
  };

  if (
    hasOfficialLetterhead === undefined &&
    nameAndLogoVisible === undefined &&
    officiallySigned === undefined &&
    hasDate === undefined &&
    fundConditions === undefined &&
    isMoreThan6MonthsOld === undefined
  ) {
    return <div>{t('forms.finance:uploadedBy')} {uploadedBy ? uploadedBy : 'University'}</div>;
  }
  return (
    <>
      <SummaryRow
        key="hasOfficialLetterhead"
        label={t('forms.finance:officialLetterhead')}
        fullLabel="Is your letter printed on official letterhead?"
        value={hasOfficialLetterhead ? 'Yes' : 'No'}
        isRisk={showRisks && isFundingRisk(risk, 'hasOfficialLetterhead', id)}
      />

      <SummaryRow
        key="nameAndLogoVisible"
        label={t('forms.finance:nameAndLogoVisible')}
        fullLabel="Is the loan provider's name and logo visible on the letterhead?"
        value={nameAndLogoVisible ? 'Yes' : 'No'}
        isRisk={showRisks && isFundingRisk(risk, 'nameAndLogoVisible', id)}
      />

      <SummaryRow
        key="officiallySigned"
        label={t('forms.finance:officiallySigned')}
        fullLabel="Is it officially stamped and signed?"
        value={officiallySigned ? 'Yes' : 'No'}
        isRisk={showRisks && isFundingRisk(risk, 'officiallySigned', id)}
      />

      <SummaryRow
        key="hasDate"
        label={t('forms.finance:hasDate')}
        fullLabel="Is there a date on the letter?"
        value={hasDate ? 'Yes' : 'No'}
        isRisk={showRisks && isFundingRisk(risk, 'hasDate', id)}
      />

      {hasDate && (
        <SummaryRow
          key="dateOnLetter"
          label={t('forms.finance:date')}
          fullLabel="What is the date on the letter?"
          value={getFormatDateFromObject(getDateFromString(dateOnLetter))}
          isRisk={showRisks && isFundingRisk(risk, 'isMoreThan6MonthsOld', id)}
        />
      )}

      <SummaryRow
        key="selectedTextBodyReq"
        label={t('forms.finance:included')}
        fullLabel="Does the body text of the letter include:"
        value={selectedTextBodyReq ? getBodyTextAnswers() : 'N/A'}
        isRisk={showRisks && isFundingRisk(risk, 'hasAllNames', id)}
      />

      <SummaryRow
        key="fundConditions"
        label={t('forms.finance:anyConditions')}
        fullLabel="Are there any conditions on your funds? (aside from a successful Student visa application)"
        value={fundConditions ? 'Yes' : 'No'}
        isRisk={showRisks && isFundingRisk(risk, 'fundConditions', id)}
      />

      <SummaryRow
        key="hasContactDetails"
        label={t('forms.finance:hasContactDetails')}
        fullLabel="Are contact details for your loan provider included?"
        value={hasContactDetails ? 'Yes' : 'No'}
        isRisk={showRisks && isFundingRisk(risk, 'hasContactDetails', id)}
      />
    </>
  );
}
